import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Icons,
} from '@commenda/kakashi';
import {
  TFormattedWorkflowDefinitionValidationError,
  WorkflowDefinitionValidationSeverityType,
} from '@commenda/types';

type TValidationErrorsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  errors: TFormattedWorkflowDefinitionValidationError[];
  warnings: TFormattedWorkflowDefinitionValidationError[];
};

export const ValidationErrorsModal = ({
  isOpen,
  onClose,
  errors,
  warnings,
}: TValidationErrorsModalProps) => {
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;

  const renderErrorItem = (
    error: TFormattedWorkflowDefinitionValidationError,
    index: number
  ) => {
    const isError = error.severity === WorkflowDefinitionValidationSeverityType.ERROR;
    const Icon = isError ? Icons.AlertCircle : Icons.AlertTriangle;
    const iconColor = isError
      ? 'text-text-error'
      : 'text-text-warning';

    return (
      <div
        key={index}
        className="flex gap-3 p-4 border border-border-primary rounded-md"
      >
        <div className="flex-shrink-0 mt-0.5">
          <Icon className={`size-5 ${iconColor}`} />
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <div className="flex flex-col gap-1">
            <span className="txt-small-bold text-text-primary">
              {error.message || error.rule}
            </span>
            <span className="txt-extra-small-regular text-text-tertiary">
              Code: {error.code} | Rule: {error.rule}
            </span>
          </div>
          {error.details && Object.keys(error.details).length > 0 && (
            <div className="mt-2 p-2 bg-bg-secondary rounded-md">
              <span className="txt-extra-small-bold text-text-secondary mb-1 block">
                Details:
              </span>
              <pre className="txt-extra-small-regular text-text-tertiary whitespace-pre-wrap break-words">
                {JSON.stringify(error.details, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Validation Errors and Warnings</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6 overflow-y-auto flex-1 p-6">
          {hasErrors && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Icons.AlertCircle className="size-5 text-text-error" />
                <h3 className="txt-medium-bold text-text-primary">
                  Errors ({errors.length})
                </h3>
              </div>
              <div className="flex flex-col gap-3">
                {errors.map((error, index) => renderErrorItem(error, index))}
              </div>
            </div>
          )}
          {hasWarnings && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Icons.AlertTriangle className="size-5 text-text-warning" />
                <h3 className="txt-medium-bold text-text-primary">
                  Warnings ({warnings.length})
                </h3>
              </div>
              <div className="flex flex-col gap-3">
                {warnings.map((warning, index) =>
                  renderErrorItem(warning, index)
                )}
              </div>
            </div>
          )}
          {!hasErrors && !hasWarnings && (
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              <Icons.CheckCircle className="size-8 text-text-success" />
              <span className="txt-small-regular text-text-tertiary">
                No validation errors or warnings found.
              </span>
            </div>
          )}
        </div>
        <div className="flex justify-end p-6 border-t border-border-primary">
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

